/**
 * DragDropContext
 *
 * Context for managing drag-and-drop state for meal cards.
 * Tracks which meal is being dragged and the current drop target.
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { MealType, PlannedMeal } from '../redux/mealPlanSlice';

// Types for drop zone tracking
export interface DropZone {
  date: string;
  mealType: MealType;
  y: number;
  height: number;
  element?: HTMLElement; // DOM element for bounds-based hit testing
}

export interface DragOrigin {
  date: string;
  mealType: MealType;
}

export interface DropTarget {
  date: string;
  mealType: MealType;
}

interface DragDropState {
  isDragging: boolean;
  draggedMeal: PlannedMeal | null;
  dragOrigin: DragOrigin | null;
  currentDropTarget: DropTarget | null;
}

interface DragDropContextValue {
  state: DragDropState;
  dropZones: React.MutableRefObject<Map<string, DropZone>>;
  scrollOffset: React.MutableRefObject<number>;
  startDrag: (meal: PlannedMeal, origin: DragOrigin) => void;
  updateDropTarget: (target: DropTarget | null) => void;
  endDrag: () => void;
  cancelDrag: () => void;
  registerDropZone: (key: string, zone: DropZone) => void;
  unregisterDropZone: (key: string) => void;
  setScrollOffset: (offset: number) => void;
  setScrollContainerTop: (top: number) => void;
  findDropZoneAtY: (absoluteY: number) => DropTarget | null;
  findDropZoneAtPoint: (clientX: number, clientY: number) => DropTarget | null;
}

const DragDropContext = createContext<DragDropContextValue | null>(null);

export const DragDropProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<DragDropState>({
    isDragging: false,
    draggedMeal: null,
    dragOrigin: null,
    currentDropTarget: null,
  });

  // Store drop zone positions for hit testing
  const dropZones = useRef<Map<string, DropZone>>(new Map());

  // Track scroll offset for accurate hit testing
  const scrollOffset = useRef<number>(0);

  // Track scroll container's top position on screen (measured, not hardcoded)
  const scrollContainerTop = useRef<number>(0);

  const startDrag = useCallback((meal: PlannedMeal, origin: DragOrigin) => {
    setState({
      isDragging: true,
      draggedMeal: meal,
      dragOrigin: origin,
      currentDropTarget: null,
    });
  }, []);

  const updateDropTarget = useCallback((target: DropTarget | null) => {
    setState(prev => ({
      ...prev,
      currentDropTarget: target,
    }));
  }, []);

  const endDrag = useCallback(() => {
    setState({
      isDragging: false,
      draggedMeal: null,
      dragOrigin: null,
      currentDropTarget: null,
    });
  }, []);

  const cancelDrag = useCallback(() => {
    setState({
      isDragging: false,
      draggedMeal: null,
      dragOrigin: null,
      currentDropTarget: null,
    });
  }, []);

  const registerDropZone = useCallback((key: string, zone: DropZone) => {
    dropZones.current.set(key, zone);
  }, []);

  const unregisterDropZone = useCallback((key: string) => {
    dropZones.current.delete(key);
  }, []);

  // Update scroll offset (called from scrollable container)
  const setScrollOffset = useCallback((offset: number) => {
    scrollOffset.current = offset;
  }, []);

  // Update scroll container top position (measured from onLayout)
  const setScrollContainerTop = useCallback((top: number) => {
    scrollContainerTop.current = top;
  }, []);

  // Find which drop zone contains the given absolute screen coordinates
  // Uses getBoundingClientRect for reliable hit testing (works better than elementsFromPoint on mobile Safari)
  const findDropZoneAtPoint = useCallback((clientX: number, clientY: number): DropTarget | null => {
    // Add padding to hit area to be more forgiving when crossing day boundaries
    const PADDING = 8;

    // Iterate through registered drop zones and check bounds
    for (const [, zone] of dropZones.current) {
      if (zone.element) {
        const rect = zone.element.getBoundingClientRect();
        if (
          clientX >= rect.left - PADDING &&
          clientX <= rect.right + PADDING &&
          clientY >= rect.top - PADDING &&
          clientY <= rect.bottom + PADDING
        ) {
          return { date: zone.date, mealType: zone.mealType };
        }
      }
    }

    // Fallback: use data-dropzone attribute with elementsFromPoint
    if (typeof document !== 'undefined' && document.elementsFromPoint) {
      const elements = document.elementsFromPoint(clientX, clientY);
      for (const el of elements) {
        const dropZoneData = (el as HTMLElement).dataset?.dropzone;
        if (dropZoneData) {
          try {
            const { date, mealType } = JSON.parse(dropZoneData);
            return { date, mealType };
          } catch {
            // Invalid JSON, skip
          }
        }
      }
    }
    return null;
  }, []);

  // Legacy: Find which drop zone contains the given absolute Y coordinate
  // Converts absoluteY to content-relative Y using scroll offset
  const findDropZoneAtY = useCallback((absoluteY: number): DropTarget | null => {
    // Drop zones store content-relative Y positions
    // Use the measured scroll container top position (from onLayout)
    const contentY = absoluteY - scrollContainerTop.current + scrollOffset.current;

    for (const [, zone] of dropZones.current) {
      if (contentY >= zone.y && contentY <= zone.y + zone.height) {
        return { date: zone.date, mealType: zone.mealType };
      }
    }
    return null;
  }, []);

  const value: DragDropContextValue = {
    state,
    dropZones,
    scrollOffset,
    startDrag,
    updateDropTarget,
    endDrag,
    cancelDrag,
    registerDropZone,
    unregisterDropZone,
    setScrollOffset,
    setScrollContainerTop,
    findDropZoneAtY,
    findDropZoneAtPoint,
  };

  return (
    <DragDropContext.Provider value={value}>
      {children}
    </DragDropContext.Provider>
  );
};

export const useDragDrop = () => {
  const context = useContext(DragDropContext);
  if (!context) {
    throw new Error('useDragDrop must be used within a DragDropProvider');
  }
  return context;
};

export default DragDropContext;
